import type { OfferOption } from './types';

/**
 * Bonus a deposit-offer option grants, in cents. Cash-Back tokens are
 * $1-equivalent (100 cents = 1 CD), so the bonus is simply `bonusPercent` of
 * the option's deposit amount, rounded to the nearest cent. This is what the
 * api mints on `charge.succeeded`; we mirror the math client-side only to show
 * the player the concrete reward ("Deposit $20 → +◈ 5 CD") rather than a bare
 * percentage.
 */
export function offerBonusCents(option: Pick<OfferOption, 'depositCents' | 'bonusPercent'>): number {
  return Math.round((option.depositCents * option.bonusPercent) / 100);
}

/**
 * Cents → a clean Crown-Dollars count for inline reward copy ("5", "12.5").
 * Tokens are $1-equivalent; trailing zeros are trimmed so round bonuses read
 * as whole tokens while fractional ones keep up to two places.
 */
export function formatCrownDollars(cents: number): string {
  const tokens = cents / 100;
  return Number.isInteger(tokens) ? tokens.toLocaleString() : tokens.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * Highest-bonus option of an offer (drives the compact subtitle / reminder hook
 * and the offer sheet's "Best Value" tab). Ties keep the earlier option, so
 * callers that pre-sort by `position` get the first of the best.
 */
export function bestOption<T extends Pick<OfferOption, 'bonusPercent'>>(options: T[]): T | undefined {
  return options.reduce<T | undefined>(
    (best, o) => (best == null || o.bonusPercent > best.bonusPercent ? o : best),
    undefined,
  );
}

/**
 * Buckets a validity duration (seconds) into the coarsest non-zero unit —
 * `"7d"` / `"3h"` / `"45m"`. The surfaces API only exposes how long a granted
 * lot / option stays valid (`expirySeconds`), not an absolute deadline, so this
 * is a validity hint, not a live countdown. Callers add their own prefix
 * (e.g. `"Use within "`). Returns `undefined` for a missing/non-positive value.
 */
export function humanizeValidity(seconds: number | null | undefined): string | undefined {
  if (seconds == null || seconds <= 0) return undefined;
  const days = Math.floor(seconds / 86_400);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(seconds / 3_600);
  if (hours >= 1) return `${hours}h`;
  return `${Math.max(1, Math.floor(seconds / 60))}m`;
}
